import React, {useRef, useState} from 'react';
import styles from './Form.module.css';
import {useTranslation} from 'react-i18next';

import {Button, PasswordInput, TextInput} from '@mantine/core';
import {useForm} from '@mantine/form';
import {Link} from 'react-router-dom';
import useAuth from '@hooks/useAuth/useAuth';

import ReCAPTCHA from "react-google-recaptcha";

import appconf from "../../../../appconf";

import {
  emailValidator,
  mainPasswordValidater,
  nameValidator,
  organizationValidator,
  passwordValidator
} from '@utils/validators';
import Approve from './Approve';

const Form = () => {
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const recaptchaRef = useRef();
  const [capchaError, setCapchaError] = useState(null);
  const [, { register, sendAgain }] = useAuth();
  const [registerValues, setRegisterValues] = useState();
  const [success, setSuccess] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    validateInputOnBlur: true,
    initialValues: {
      email: '',
      organizationName: '',
      password: '',
      passwordAgain: '',
      name: '',
    },

    validate: {
      name: (value) => nameValidator(value, t),
      organizationName: (value) => organizationValidator(value, t),
      email: (value) => emailValidator(value, t),
      passwordAgain: (value, { password }) => passwordValidator(value, { password }, t),
      password: (value) => mainPasswordValidater(value, t)
    },
  });

  const isClearObject = (object) => JSON.stringify(object) === '{}'

  const onSuccess = (values) => {
    setSuccess(true);

    setRegisterValues(values)
    
    window.gtag('event', 'action_registration')
  }

  const handleSubmit = async (values) => {
    setLoader(true);
    setCapchaError(null);
    const token = await recaptchaRef.current.getValue();

    if (!token) {
      setCapchaError(t('captcha.error'))

      setLoader(false);
      return;
    }

    const submitErrors = await register({
      ...values,
      reToken: token
    }, () => onSuccess(values));

    if (submitErrors?.length > 0) {
      submitErrors.forEach(error => {
        switch (error.code) {
          case 'P010':
            recaptchaRef.current.reset();
            setCapchaError(t('captcha.verError'));
            break;

          case 'P100':
            recaptchaRef.current.reset();
            form.setErrors({
              email: t('mail.error', { email: values.email })
            })

            break;

          default:
            break;
        }
      })
    }

    setLoader(false);
  }

  const toRegister = () => {
    setSuccess(false)
  }

  const sendAgaing = async () => {
    const email = registerValues.email
    await sendAgain({ email });
  }

  const handleChange = () => {
    setCapchaError(null)
  }

  const handleBlur = (type) => {
    const value = form.getValues()[type]
    const data = value.replace(/^(\s|\u00A0)+|(\s|\u00A0)+$/g, "")

    form.setValues({
      [type]: data
    })

    const error = form.validateField(type)

    if (error) {
      form.setErrors({ [type]: error.error });
    }
  }

  return (
    <div className={styles.wrapper}>
      {!success && (
        <div>
          <div className={styles.title}>
            {t('signIn')}
          </div>

          <form
            className={styles.form}
            onSubmit={form.onSubmit(handleSubmit)}
          >
            <TextInput
              label={t('name.label')}
              placeholder={t('name.placeholder')}
              key={form.key('name')}
              {...form.getInputProps('name')}
              onBlur={() => handleBlur('name')}
            />

            <div className={styles.separator} />

            <TextInput
              label={t('company.label')}
              placeholder={t('company.placeholder')}
              key={form.key('organizationName')}
              {...form.getInputProps('organizationName')}
              onBlur={() => handleBlur('organizationName')}
            />

            <div className={styles.separator} />

            <TextInput
              label={t('email.label')}
              placeholder={t('email.placeholder')}
              key={form.key('email')}
              {...form.getInputProps('email')}
              onBlur={() => handleBlur('email')}
            />

            <div className={styles.separator} />

            <PasswordInput
              label={t('password.label')}
              key={form.key('password')}
              password
              placeholder={t('password.placeholder')}
              {...form.getInputProps('password')}
            />

            <div className={styles.separator} />

            <PasswordInput
              label={t('password.again.label')}
              key={form.key('passwordAgain')}
              placeholder={t('password.placeholder')}
              password
              {...form.getInputProps('passwordAgain')}
            />

            <div className={styles.separator} />

            <div className={styles.policy}>
              {t('policy.start')}

              <Link to={process.env.PUBLIC_URL + "/agreement.pdf"} reloadDocument={true}>
                <span className={styles.policyLink}> {t('policy.agree')} </span>
              </Link>

              {t('and')}

              <Link to={process.env.PUBLIC_URL + "/policy.pdf"} reloadDocument={true}>
                <span className={styles.policyLink} >{t('policy.confident')}</span>
              </Link>
            </div>

            <div className={styles.capcha}>
              <ReCAPTCHA
                ref={recaptchaRef}
                lang='en'
                hl='en'
                sitekey={appconf.captcha.SITE_KEY}
                onChange={handleChange}
              />

              {capchaError && (
                <div className={styles.capchaError}>
                  {capchaError}
                </div>
              )}
            </div>

            <Button
              className={styles.button}
              fullWidth
              type="submit"
              loading={loader}
              disabled={!isClearObject(form.errors)}
            >
              {t('signIn.submit')}
            </Button>
          </form>

          <Link to='/log_in' className={styles.buttonLink}>
            <div className={styles.buttonLink}>
              {t('signIn.button.hasAcc')}
            </div>
          </Link>
        </div>
      )}

      {success && (
        <Approve
          toRegister={toRegister}
          sendAgaing={sendAgaing}
          email={registerValues.email}
        />
      )}
    </div >
  )
}

export default Form;
